import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { useAccount, useConnect, useDisconnect } from "wagmi";
import { useMediaQuery } from "@mui/material";
import { PhantomWalletName } from "@solana/wallet-adapter-phantom";
import { SolflareWalletName } from "@solana/wallet-adapter-solflare";
import _ from "lodash";
import { useTheme } from "@mui/styles";
import useTrackEvent from "./useTrackEvent";
import { selectUserData } from "features/home/redux/home.selector";
import { useSelector } from "react-redux";
import {
  MartianWalletName,
  AptosWalletName,
  RiseWalletName,
  PontemWalletName,
  useWallet as useAptosWallet,
} from "@manahippo/aptos-wallet-adapter";

export default function useConnectWallet({ ecosystem }) {
  const pathname = window.location.pathname;
  const isOnboarding = pathname === "/onboarding";

  const [connectorList, setConnectorList] = useState([]);
  const [walletType, setWalletType] = useState("");
  const { address: globalAddress } = useSelector(selectUserData) || {
    address: "",
  };

  const theme = useTheme();
  const computer = useMediaQuery(theme.breakpoints.up("sm"));

  const connectorsImg = {
    metaMask: require("assets/img/MetaMask.png"),
    injected: require("assets/img/BrowserWallet.png"),
    walletConnect: require("assets/img/WalletConnect.svg").default,
    [MartianWalletName]: require("assets/img/martian.png"),
    [RiseWalletName]: require("assets/img/rise.png"),
    [AptosWalletName]: require("assets/img/petra.png"),
    [PontemWalletName]: require("assets/img/pontem.svg").default,
    [PhantomWalletName]: require("assets/img/phantom.png"),
    [SolflareWalletName]: require("assets/img/solflare.png"),
  };

  // EVM Wallet
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();
  const {
    address: evmAddress,
    isConnected: evmConnected,
    isConnecting: evmConnecting,
  } = useAccount({});

  const {
    connect: aptosConnect,
    disconnect: aptosDisconnect,
    connecting: aptosConnecting,
    connected: aptosConnected,
    account: aptosAccount,
    signMessage: aptosSignMessage,
  } = useAptosWallet();

  // Solana Wallet
  const {
    select: solanaSelect,
    connect: solanaConnect,
    disconnect: solanaDisconnect,
    connecting: solanaConnecting,
    connected: solanaConnected,
    publicKey: solanaAccount,
    signMessage: solanaSignMessage,
  } = useSolanaWallet();

  // get init connectors
  function getInitConnector() {
    if (ecosystem === "EVM") {
      let filteredConnectors = connectors;
      if (!computer) {
        filteredConnectors = _.filter(filteredConnectors, function (connector) {
          return connector.name !== "MetaMask";
        });
      }
      if (!window?.ethereum) {
        filteredConnectors = _.filter(filteredConnectors, function (connector) {
          return connector.name == "WalletConnect";
        });
      }

      const orderedConnectors = _.sortBy(filteredConnectors, (connector) => {
        return ["MetaMask", "Browser Wallet", "WalletConnect"].indexOf(
          connector.name
        );
      }).map((item) => ({
        connectItem: item,
        name: item.name,
        img: connectorsImg[item.id],
      }));

      setConnectorList(orderedConnectors);
    }
    if (ecosystem === "Solana") {
      const connectors = [PhantomWalletName, SolflareWalletName].map(
        (item) => ({
          connectItem: item,
          name: item,
          img: connectorsImg[item],
        })
      );
      setConnectorList(connectors);
    }
    if (ecosystem === "Aptos") {
      const connectors = [
        PontemWalletName,
        MartianWalletName,
        RiseWalletName,
        AptosWalletName,
      ].map((item) => ({
        connectItem: item,
        name: item,
        img: connectorsImg[item],
      }));
      setConnectorList(connectors);
    }
  }

  useEffect(() => {
    if (!ecosystem) return;
    getInitConnector();
  }, [ecosystem, computer]);

  const trackEvent = useTrackEvent(isOnboarding);

  async function handleConnectWallet(connector) {
    if (ecosystem === "EVM") {
      setWalletType(connector.name);
      trackEvent("onboarding_wallet_connect_click", {
        wallet_type: connector.name,
      });
      try {
        await connect({ connector: connector.connectItem });
        setWalletType(connector.name);
      } catch (error) {
        trackEvent("onboarding_wallet_connect_error", {
          wallet_type: connector.name,
          error_message: error.message,
        });
      }
    }
    if (ecosystem === "Solana") {
      setWalletType(connector.name);
      trackEvent("onboarding_wallet_connect_click", {
        wallet_type: connector.name,
      });
      try {
        await solanaSelect(connector.connectItem);
        setWalletType(connector.name);
      } catch (error) {
        trackEvent("onboarding_wallet_connect_error", {
          wallet_type: connector.name,
          error_message: error.message,
        });
      }
    }
    if (ecosystem === "Aptos") {
      setWalletType(connector.name);
      trackEvent("onboarding_wallet_connect_click", {
        wallet_type: connector.name,
      });
      try {
        await aptosConnect(connector.connectItem);
        setWalletType(connector.name);
      } catch (error) {
        console.log(error);
        trackEvent("onboarding_wallet_connect_error", {
          wallet_type: connector.name,
          error_message: error.message,
        });
      }
    }
  }

  const address = useMemo(() => {
    if (ecosystem === "Solana") return solanaAccount?.toBase58();
    if (ecosystem === "Aptos") return aptosAccount?.address;
    if (ecosystem === "EVM") return evmAddress;
    return globalAddress;
  }, [ecosystem, solanaAccount, aptosAccount, evmAddress, globalAddress]);

  // change to useMemo isConnected and isConnecting
  const isConnected = useMemo(() => {
    if (ecosystem === "Solana") return solanaConnected;
    if (ecosystem === "Aptos") return aptosConnected;
    if (ecosystem === "EVM") return evmConnected;
    return true;
  }, [ecosystem, solanaConnected, aptosConnected, evmConnected]);

  const isConnecting = useMemo(() => {
    if (ecosystem === "Solana") return solanaConnecting;
    if (ecosystem === "Aptos") return aptosConnecting;
    if (ecosystem === "EVM") return evmConnecting;
    return false;
  }, [ecosystem, solanaConnecting, aptosConnecting, evmConnecting]);

  const handleDisconnectWallet = async () => {
    if (ecosystem === "Solana") {
      await solanaDisconnect();
    } else if (ecosystem === "Aptos") {
      await aptosDisconnect();
    } else {
      await disconnect();
    }
    trackEvent("onboarding_wallet_disconnected");
    setWalletType("");
  };

  return {
    connectorList,
    address,
    isConnected,
    isConnecting,
    handleDisconnectWallet,
    handleConnectWallet,
    walletType,
  };
}
